import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Power } from "lucide-react";

const EMPTY = {
  name: "", description: "",
  min_amount: 100, max_amount: 1000,
  min_duration: 1, max_duration: 12,
  duration_unit: "months", interest_type: "monthly",
  interest_rate: 5, service_fee: 0, processing_fee: 0,
  late_payment_fee: 25, penalty_rules: "",
  min_income: 0, employment_required: false,
  required_documents: ["Government ID"],
  ekyc_required: true, bank_nomination_required: true,
  auto_approval: false, manual_review_required: true, active: true,
};

export default function LoanProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/loan-products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e?.target?.type === "number" ? Number(e.target.value) : (e?.target?.value ?? e);
    setForm({ ...form, [k]: v });
  };

  const save = async () => {
    try {
      const payload = { ...form };
      payload.required_documents = Array.isArray(payload.required_documents) ? payload.required_documents : String(payload.required_documents).split(',').map(s=>s.trim()).filter(Boolean);
      if (editing) await api.put(`/loan-products/${editing.id}`, payload);
      else await api.post("/loan-products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (p) => {
    if (p.active) {
      await api.delete(`/loan-products/${p.id}`);
    } else {
      await api.put(`/loan-products/${p.id}`, { ...p, active: true });
    }
    load();
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, required_documents: Array.isArray(p.required_documents) ? p.required_documents.join(", ") : "" });
    setOpen(true);
  };

  return (
    <Layout>
      <div className="fade-in space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Loan products</h1>
          <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800" data-testid="new-product-btn"><Plus className="h-4 w-4 mr-1"/> New product</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit product" : "New loan product"}</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                <F label="Name"><Input data-testid="product-name" value={form.name} onChange={onChange("name")}/></F>
                <F label="Description"><Input data-testid="product-desc" value={form.description} onChange={onChange("description")}/></F>
                <F label="Min amount ($)"><Input type="number" data-testid="product-min-amt" value={form.min_amount} onChange={onChange("min_amount")}/></F>
                <F label="Max amount ($)"><Input type="number" data-testid="product-max-amt" value={form.max_amount} onChange={onChange("max_amount")}/></F>
                <F label="Min duration"><Input type="number" data-testid="product-min-dur" value={form.min_duration} onChange={onChange("min_duration")}/></F>
                <F label="Max duration"><Input type="number" data-testid="product-max-dur" value={form.max_duration} onChange={onChange("max_duration")}/></F>
                <F label="Duration unit">
                  <Select value={form.duration_unit} onValueChange={(v)=>setForm({...form, duration_unit: v})}>
                    <SelectTrigger data-testid="product-dur-unit"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                      <SelectItem value="months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Interest type">
                  <Select value={form.interest_type} onValueChange={(v)=>setForm({...form, interest_type: v})}>
                    <SelectTrigger data-testid="product-int-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="annualized">Annualized</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Interest rate (%)"><Input type="number" data-testid="product-int-rate" value={form.interest_rate} onChange={onChange("interest_rate")}/></F>
                <F label="Service fee"><Input type="number" data-testid="product-svc-fee" value={form.service_fee} onChange={onChange("service_fee")}/></F>
                <F label="Processing fee"><Input type="number" data-testid="product-proc-fee" value={form.processing_fee} onChange={onChange("processing_fee")}/></F>
                <F label="Late payment fee"><Input type="number" data-testid="product-late-fee" value={form.late_payment_fee} onChange={onChange("late_payment_fee")}/></F>
                <F label="Min monthly income"><Input type="number" data-testid="product-min-income" value={form.min_income} onChange={onChange("min_income")}/></F>
                <F label="Required documents (comma-separated)" className="sm:col-span-2"><Input data-testid="product-req-docs" value={form.required_documents} onChange={onChange("required_documents")}/></F>
                <F label="Penalty rules" className="sm:col-span-2"><Textarea data-testid="product-penalty" value={form.penalty_rules} onChange={onChange("penalty_rules")}/></F>
                <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
                  <Toggle label="eKYC required" value={form.ekyc_required} onChange={(v)=>setForm({...form, ekyc_required: v})} testId="product-ekyc"/>
                  <Toggle label="Bank nomination required" value={form.bank_nomination_required} onChange={(v)=>setForm({...form, bank_nomination_required: v})} testId="product-bank"/>
                  <Toggle label="Employment required" value={form.employment_required} onChange={(v)=>setForm({...form, employment_required: v})} testId="product-emp"/>
                  <Toggle label="Manual review required" value={form.manual_review_required} onChange={(v)=>setForm({...form, manual_review_required: v})} testId="product-manual"/>
                  <Toggle label="Auto approval" value={form.auto_approval} onChange={(v)=>setForm({...form, auto_approval: v})} testId="product-auto"/>
                  <Toggle label="Active" value={form.active} onChange={(v)=>setForm({...form, active: v})} testId="product-active"/>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="save-product-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Limits</th>
                  <th className="text-left px-4 py-2">Duration</th>
                  <th className="text-left px-4 py-2">Interest</th>
                  <th className="text-left px-4 py-2">Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{p.name}</td>
                    <td className="px-4 py-2">${p.min_amount}–${p.max_amount}</td>
                    <td className="px-4 py-2">{p.min_duration}–{p.max_duration} {p.duration_unit}</td>
                    <td className="px-4 py-2">{p.interest_rate}% {p.interest_type}</td>
                    <td className="px-4 py-2">{p.active ? "Yes" : "No"}</td>
                    <td className="px-4 py-2 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4"/></Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(p)} data-testid={`toggle-product-${p.id}`}><Power className="h-4 w-4"/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>
    </Layout>
  );
}

function F({label, children, className}) {
  return <div className={className}><Label className="mb-1 block">{label}</Label>{children}</div>;
}
function Toggle({label, value, onChange, testId}) {
  return (
    <label className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
      <span>{label}</span>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId}/>
    </label>
  );
}
